import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Content } from "../../components/wrappers/Content";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { useFetch } from "../../lib/useFetch";
import { cn } from "../../lib/utils";
import { fmtMoney, Stat } from "../Home/shared";
import { PaymentStatePill, PayViaRevolutDialog } from "./PayViaRevolutDialog";

/**
 * Shared receivables/payables screen (Phase D, ws-screens.js + the money-screens
 * gap analysis): view segments with counts, brand + charter-family filters,
 * search, £ totals with per-currency chips. Reads the replicated Xero ledger.
 * Receivables additionally carry the chase trail (ops parity): a Last-reminder
 * column, per-row and bulk "Remind" with an editable email + self-preview, and
 * "Log a chase" for calls/WhatsApp done outside the app.
 */

export type OpenInvoiceRow = {
  invoiceId: string;
  invoiceNumber: string | null;
  invoiceCategory: string | null;
  contactName: string | null;
  dealName: string | null;
  yachtName: string | null;
  charterType: string | null;
  dealStartDate: string | null;
  brand: string | null;
  currency: string | null;
  amountDue: number;
  amountTotal: number;
  amountPaid: number;
  amountDueGbp: number | null;
  invoiceDate: string | null;
  dueDate: string | null;
  hsDealId: string | null;
  bookingId: string | null;
  bookingSource: string | null;
  clientEmail: string | null;
  paymentUrl: string | null;
  clientBilled: number | null;
  clientPaid: number | null;
  shortfall: number | null;
  fundingState: FundingState | null;
  /** Payables: supplier bank details on file — a bill can't be paid without them. */
  supplierBankOnFile: boolean | null;
  charterInDays: number | null;
  category: string;
  categoryLabel: string;
  /** Owed by the operator: never chased with a client reminder, not funding-gated. */
  isCommission: boolean;
  lastReminderAt: string | null;
  lastReminderStatus: string | null;
  lastReminderChannel: string | null;
  reminderCount: number;
  supplierPayment?: { id: string; state: string; amount: number; currency: string; createdAt: string | null; error: string | null } | null;
};

export type FundingState = "ready_deposit" | "ready_balance" | "client_overdue" | "waiting" | "unlinked";

const FUNDING_LABEL: Record<FundingState, { label: string; className: string }> = {
  ready_balance: { label: "Balance ready", className: "bg-green-100 text-green-800" },
  ready_deposit: { label: "Deposit ready", className: "bg-blue-100 text-blue-800" },
  client_overdue: { label: "Client overdue", className: "bg-red-100 text-red-700" },
  waiting: { label: "Awaiting client", className: "bg-amber-100 text-amber-800" },
  unlinked: { label: "No booking", className: "bg-slate-100 text-slate-600" },
};

type SettledRow = {
  invoiceId: string;
  invoiceNumber: string | null;
  contactName: string | null;
  dealName: string | null;
  yachtName: string | null;
  brand: string | null;
  currency: string | null;
  amountTotal: number;
  status: string | null;
  invoiceDate: string | null;
  bookingId: string | null;
};

type Segment = "overdue" | "due_soon" | "future" | "all";
const SEGMENTS: [Segment, string][] = [
  ["overdue", "Overdue"],
  ["due_soon", "Due ≤14d"],
  ["future", "Future"],
  ["all", "All"],
];
const FAMILIES = ["", "bareboat", "skippered", "crewed"] as const;

const segmentOf = (r: OpenInvoiceRow): Segment => {
  if (!r.dueDate) return "future";
  const due = new Date(r.dueDate).getTime();
  const now = Date.now();
  if (due < now) return "overdue";
  if (due < now + 14 * 86400000) return "due_soon";
  return "future";
};

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

/** "3 days ago" / "in 2 weeks" — chase recency reads better relative. */
const fmtRelative = (iso?: string | null) => {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
};

export function OpenInvoicesScreen({ kind }: { kind: "receivables" | "payables" }) {
  const { fetch } = useFetch();
  const navigate = useNavigate();
  const [rows, setRows] = useState<OpenInvoiceRow[] | null>(null);
  const [segment, setSegment] = useState<Segment>("all");
  const [brand, setBrand] = useState("");
  const [family, setFamily] = useState("");
  const [search, setSearch] = useState("");
  const [linkFilter, setLinkFilter] = useState<"" | "has_link" | "no_link">("");
  const [sort, setSort] = useState<"due_date" | "start_date" | "most_overdue">("due_date");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [remindRows, setRemindRows] = useState<OpenInvoiceRow[] | null>(null);
  const [logRow, setLogRow] = useState<OpenInvoiceRow | null>(null);
  const [historyRow, setHistoryRow] = useState<OpenInvoiceRow | null>(null);
  const [markPaidRow, setMarkPaidRow] = useState<OpenInvoiceRow | null>(null);
  const [payRow, setPayRow] = useState<OpenInvoiceRow | null>(null);
  // Payables: funding lens + the settled ("sent payments") view.
  const [funding, setFunding] = useState<"" | "ready" | "waiting">("");
  const [view, setView] = useState<"open" | "settled">("open");
  const [settled, setSettled] = useState<SettledRow[] | null>(null);
  const canChase = kind === "receivables";

  const load = useCallback(() => {
    fetch<{ data: OpenInvoiceRow[] }>(`/api/finance/${kind}`)
      .then((r) => setRows(r.data ?? []))
      .catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);
  useEffect(load, [load]);

  useEffect(() => {
    if (view !== "settled" || settled !== null) return;
    fetch<{ data: SettledRow[] }>(`/api/finance/settled/${kind}`)
      .then((r) => setSettled(r.data ?? []))
      .catch(() => setSettled([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, kind]);

  const scoped = useMemo(
    () =>
      (rows ?? []).filter(
        (r) =>
          (!brand || r.brand === brand) &&
          (!family || (r.charterType ?? "").toLowerCase().includes(family)) &&
          (!linkFilter || (linkFilter === "has_link" ? !!r.paymentUrl : !r.paymentUrl)) &&
          (!funding ||
            (funding === "ready"
              ? r.fundingState === "ready_deposit" || r.fundingState === "ready_balance"
              : r.fundingState === "waiting" || r.fundingState === "client_overdue")) &&
          (!search ||
            [r.contactName, r.dealName, r.yachtName, r.invoiceNumber]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(search.toLowerCase()))),
      ),
    [rows, brand, family, linkFilter, funding, search],
  );

  const counts = useMemo(() => {
    const c: Record<Segment, number> = { overdue: 0, due_soon: 0, future: 0, all: scoped.length };
    for (const r of scoped) c[segmentOf(r)]++;
    return c;
  }, [scoped]);

  const visible = useMemo(() => {
    const pool = segment === "all" ? scoped : scoped.filter((r) => segmentOf(r) === segment);
    const key = (r: OpenInvoiceRow) =>
      sort === "start_date" ? r.dealStartDate : sort === "most_overdue" ? r.dueDate : r.dueDate;
    return [...pool].sort((a, b) => {
      const av = key(a);
      const bv = key(b);
      if (!av) return 1;
      if (!bv) return -1;
      // most_overdue = oldest due date first; the others read ascending too.
      return av.localeCompare(bv);
    });
  }, [scoped, segment, sort]);

  const totals = useMemo(() => {
    let gbp = 0;
    let allConverted = true;
    const perCurrency = new Map<string, number>();
    for (const r of visible) {
      if (r.amountDueGbp == null) allConverted = false;
      else gbp += r.amountDueGbp;
      const key = r.currency ?? "?";
      perCurrency.set(key, (perCurrency.get(key) ?? 0) + r.amountDue);
    }
    return { gbp, allConverted, perCurrency: [...perCurrency.entries()] };
  }, [visible]);

  const title = kind === "receivables" ? "Receivables" : "Payables";
  const who = kind === "receivables" ? "Client" : "Supplier";

  const fundingCounts = useMemo(() => {
    let ready = 0;
    let waiting = 0;
    let readyValueGbp = 0;
    for (const r of rows ?? []) {
      if (r.fundingState === "ready_deposit" || r.fundingState === "ready_balance") {
        ready++;
        readyValueGbp += r.amountDueGbp ?? 0;
      } else if (r.fundingState === "waiting" || r.fundingState === "client_overdue") waiting++;
    }
    return { ready, waiting, readyValueGbp };
  }, [rows]);

  const selectedRows = useMemo(() => (rows ?? []).filter((r) => selected.has(r.invoiceId)), [rows, selected]);
  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const toggleAllVisible = (on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of visible) {
        if (!on) next.delete(r.invoiceId);
        // Commission invoices are owed by the operator, not the client.
        else if (!r.isCommission) next.add(r.invoiceId);
      }
      return next;
    });
  const allVisibleSelected = visible.length > 0 && visible.filter((r) => !r.isCommission).every((r) => selected.has(r.invoiceId));

  return (
    <Content title={title}>
      <div className="flex flex-col gap-4 p-1">
        {canChase && selectedRows.length > 0 && (
          <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
            <span className="text-sm">
              <span className="font-semibold">{selectedRows.length} selected</span>
              <span className="ml-2 text-slate-500">
                {fmtMoney(selectedRows.reduce((t, r) => t + (r.amountDueGbp ?? 0), 0), "GBP")} outstanding
              </span>
              {selectedRows.length > 50 && <span className="ml-2 font-medium text-amber-700">max 50 per send</span>}
            </span>
            <span className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
              <Button size="sm" disabled={selectedRows.length > 50} onClick={() => setRemindRows(selectedRows)}>
                Send payment reminder to {selectedRows.length}
              </Button>
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat
            label={`Outstanding (${visible.length} invoices)`}
            value={`${totals.allConverted ? "" : "≥ "}${fmtMoney(totals.gbp, "GBP")}`}
            detail={
              <span className="flex flex-wrap gap-1.5">
                {totals.perCurrency.map(([code, amt]) => (
                  <span key={code} className="rounded-full bg-slate-100 px-2 py-0.5 tabular-nums">
                    {fmtMoney(amt, code === "?" ? "EUR" : code)}
                  </span>
                ))}
              </span>
            }
          />
          <Stat label="Overdue" value={counts.overdue} tone={counts.overdue ? "bad" : "good"} detail="past due date" />
          {canChase ? (
            <>
              <Stat label="Due ≤14 days" value={counts.due_soon} detail="needs attention this fortnight" />
              <Stat label="Future" value={counts.future} detail="not yet due" />
            </>
          ) : (
            <>
              <Stat
                label="Ready to pay"
                value={fundingCounts.ready}
                tone="good"
                detail={`${fmtMoney(fundingCounts.readyValueGbp, "GBP")} — client funds in`}
              />
              <Stat
                label="Awaiting client funds"
                value={fundingCounts.waiting}
                tone={fundingCounts.waiting ? "bad" : "good"}
                detail="don't pay these yet"
              />
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!canChase && (
            <>
              {([["open", "Outstanding"], ["settled", "Sent payments"]] as const).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setView(k)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm",
                    view === k ? "border-blue-600 bg-blue-600 text-white" : "bg-white hover:bg-slate-50",
                  )}
                >
                  {label}
                </button>
              ))}
              <span className="mx-1 h-5 w-px bg-slate-200" />
            </>
          )}
          {SEGMENTS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setSegment(k)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm",
                segment === k ? "border-blue-600 bg-blue-600 text-white" : "bg-white hover:bg-slate-50",
              )}
            >
              {label}
              <span className={cn("rounded-full px-1.5 text-[11px] font-semibold leading-4", segment === k ? "bg-white/20" : "bg-slate-100")}>
                {counts[k]}
              </span>
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" />
          {["", "helm", "anchor"].map((b) => (
            <button
              key={b || "all"}
              onClick={() => setBrand(b)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm",
                brand === b ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50",
              )}
            >
              {b ? b[0].toUpperCase() + b.slice(1) : "Both"}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" />
          {FAMILIES.map((f) => (
            <button
              key={f || "any"}
              onClick={() => setFamily(f)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm",
                family === f ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50",
              )}
            >
              {f ? f[0].toUpperCase() + f.slice(1) : "Any type"}
            </button>
          ))}
          {!canChase && (
            <>
              <span className="mx-1 h-5 w-px bg-slate-200" />
              {(
                [
                  ["", "All bills"],
                  ["ready", `Ready to pay (${fundingCounts.ready})`],
                  ["waiting", `Awaiting funds (${fundingCounts.waiting})`],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k || "all"}
                  onClick={() => setFunding(k as any)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm",
                    funding === k
                      ? k === "waiting"
                        ? "border-amber-600 bg-amber-600 text-white"
                        : "border-slate-800 bg-slate-800 text-white"
                      : "bg-white hover:bg-slate-50",
                  )}
                >
                  {label}
                </button>
              ))}
            </>
          )}
          {canChase && (
            <>
              <span className="mx-1 h-5 w-px bg-slate-200" />
              {([["", "Any link"], ["has_link", "Has pay link"], ["no_link", "No link"]] as const).map(([k, label]) => (
                <button
                  key={k || "any"}
                  onClick={() => setLinkFilter(k as any)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm",
                    linkFilter === k ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50",
                  )}
                >
                  {label}
                </button>
              ))}
            </>
          )}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as any)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-blue-400"
          >
            <option value="due_date">Sort: due date</option>
            <option value="start_date">Sort: charter start</option>
            <option value="most_overdue">Sort: most overdue</option>
          </select>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${who.toLowerCase()}, deal, yacht, invoice…`}
            className="ml-auto w-72 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
          />
        </div>

        {view === "settled" ? (
          <SettledTable rows={settled} who={who} onOpen={(id) => navigate(`/bookings/${id}?tab=xero`)} />
        ) : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {canChase && (
                  <th className="w-8 px-3 py-2">
                    <input type="checkbox" checked={allVisibleSelected} onChange={(e) => toggleAllVisible(e.target.checked)} />
                  </th>
                )}
                <th className="px-3 py-2">{who}</th>
                <th className="px-3 py-2">Deal / yacht</th>
                <th className="px-3 py-2">Brand</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Invoice</th>
                <th className="px-3 py-2">Due</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Amount due</th>
                {!canChase && <th className="px-3 py-2 text-right">Client funds</th>}
                {!canChase && <th className="px-3 py-2 text-right">Charter in</th>}
                {!canChase && <th className="px-3 py-2">Status</th>}
                {canChase && <th className="px-3 py-2">Pay link</th>}
                {canChase && <th className="px-3 py-2">Last reminder</th>}
                <th className="px-3 py-2">{canChase ? "Actions" : "Booking"}</th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400">Nothing here — all settled.</td></tr>
              ) : (
                visible.map((r) => {
                  const seg = segmentOf(r);
                  return (
                    <tr
                      key={r.invoiceId}
                      className={cn("border-t", seg === "overdue" && "border-l-2 border-l-red-500")}
                    >
                      {canChase && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected.has(r.invoiceId)}
                            disabled={r.isCommission}
                            title={r.isCommission ? "Commission invoice — owed by the operator, not chased with client reminders" : undefined}
                            onChange={(e) => toggle(r.invoiceId, e.target.checked)}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2 font-medium">{r.contactName ?? "—"}</td>
                      <td className="max-w-[260px] truncate px-3 py-2 text-slate-600">
                        {[r.dealName, r.yachtName].filter(Boolean).join(" · ") || "—"}
                        {r.dealStartDate && <span className="block text-xs text-slate-400">departs {fmtDate(r.dealStartDate)}</span>}
                      </td>
                      <td className="px-3 py-2 capitalize text-slate-500">{r.brand ?? "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.isCommission ? (
                          <span className="rounded-full bg-violet-100 px-2 py-0.5 font-medium text-violet-800">{r.categoryLabel}</span>
                        ) : (
                          <span className="text-slate-500">{r.categoryLabel}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{r.invoiceNumber ?? "—"}</td>
                      <td className={cn("whitespace-nowrap px-3 py-2", seg === "overdue" ? "font-medium text-red-600" : "text-slate-600")}>
                        {fmtDate(r.dueDate)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-400">
                        {r.amountPaid > 0 ? fmtMoney(r.amountPaid, r.currency ?? "EUR") : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">{fmtMoney(r.amountDue, r.currency ?? "EUR")}</td>
                      {!canChase && (
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          {r.clientPaid == null ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <>
                              <span className="tabular-nums text-slate-700">{fmtMoney(r.clientPaid, r.currency ?? "EUR")}</span>
                              {r.clientBilled != null && (
                                <span className="block text-[11px] text-slate-400">
                                  of {fmtMoney(r.clientBilled, r.currency ?? "EUR")} billed
                                </span>
                              )}
                              {r.shortfall != null && r.shortfall > 0 ? (
                                <span className="mt-0.5 inline-block rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                  short {fmtMoney(r.shortfall, r.currency ?? "EUR")}
                                </span>
                              ) : (
                                <span className="mt-0.5 inline-block rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800">
                                  covers this bill
                                </span>
                              )}
                            </>
                          )}
                        </td>
                      )}
                      {!canChase && (
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          {r.charterInDays == null ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : r.charterInDays < 0 ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                              {Math.abs(r.charterInDays)}d ago
                            </span>
                          ) : (
                            <span className={cn("text-sm", r.charterInDays <= 14 && "font-medium text-amber-700")}>
                              {r.charterInDays === 0 ? "today" : `${r.charterInDays}d`}
                            </span>
                          )}
                        </td>
                      )}
                      {!canChase && (
                        <td className="px-3 py-2">
                          {r.fundingState && (
                            <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", FUNDING_LABEL[r.fundingState].className)}>
                              {FUNDING_LABEL[r.fundingState].label}
                            </span>
                          )}
                          {r.supplierBankOnFile === false && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                              Bank details missing
                            </span>
                          )}
                          {!r.fundingState && r.isCommission && (
                            <span
                              className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800"
                              title="Commission — not gated on client funds"
                            >
                              Commission
                            </span>
                          )}
                        </td>
                      )}
                      {canChase && (
                        <td className="px-3 py-2">
                          {r.paymentUrl ? (
                            <a
                              href={`${r.paymentUrl}?variant=payment`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 hover:bg-green-200"
                            >
                              Pay now
                            </a>
                          ) : (
                            <span className="text-xs text-slate-400">bank transfer</span>
                          )}
                        </td>
                      )}
                      {canChase && (
                        <td className="whitespace-nowrap px-3 py-2">
                          {r.lastReminderAt ? (
                            <button className="text-left hover:underline" onClick={() => setHistoryRow(r)}>
                              <span className="text-xs text-slate-600">{fmtRelative(r.lastReminderAt)}</span>
                              {r.reminderCount > 0 && (
                                <span
                                  className={cn(
                                    "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                                    r.reminderCount >= 3 ? "bg-red-100 text-red-700" : r.reminderCount >= 2 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600",
                                  )}
                                >
                                  {r.reminderCount}×
                                </span>
                              )}
                              <span className="block text-[11px] text-slate-400">
                                {r.lastReminderChannel === "manual"
                                  ? "logged"
                                  : r.lastReminderChannel === "platform"
                                    ? "auto instalment"
                                    : r.lastReminderStatus === "failed"
                                      ? "send failed"
                                      : "emailed"}
                              </span>
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">never</span>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-2">
                        <span className="flex items-center gap-2">
                          {canChase && (
                            <>
                              {r.isCommission ? (
                                <span
                                  className="text-xs text-violet-700"
                                  title="A commission invoice is owed by the operator — client payment reminders don't apply"
                                >
                                  Operator owes
                                </span>
                              ) : (
                                <button
                                  className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
                                  onClick={() => setRemindRows([r])}
                                >
                                  Remind
                                </button>
                              )}
                              <button className="text-xs text-slate-500 hover:text-slate-800 hover:underline" onClick={() => setLogRow(r)}>
                                Log chase
                              </button>
                            </>
                          )}
                          {kind === "payables" && !r.isCommission && (
                            r.supplierPayment && !["payment_failed", "draft_rejected", "cancelled"].includes(r.supplierPayment.state) ? (
                              <button type="button" onClick={() => setPayRow(r)} title={r.supplierPayment.error ?? "Open the Revolut payment"}>
                                <PaymentStatePill state={r.supplierPayment.state} />
                              </button>
                            ) : (
                              <button
                                className={cn("rounded-md px-2.5 py-1 text-xs font-medium text-white", r.fundingState === "ready_deposit" || r.fundingState === "ready_balance" ? "bg-slate-900 hover:bg-slate-800" : "bg-slate-400 hover:bg-slate-500")}
                                title={r.fundingState === "waiting" || r.fundingState === "client_overdue" ? "The client hasn't funded this yet" : "Create a Revolut payment draft for this bill"}
                                onClick={() => setPayRow(r)}
                              >
                                Pay via Revolut
                              </button>
                            )
                          )}
                          <button
                            className={cn(
                              "text-xs hover:underline",
                              !canChase && (r.fundingState === "waiting" || r.fundingState === "client_overdue")
                                ? "text-amber-700 hover:text-amber-800"
                                : "text-slate-500 hover:text-slate-800",
                            )}
                            title={
                              !canChase && (r.fundingState === "waiting" || r.fundingState === "client_overdue")
                                ? "The client hasn't funded this yet — you'll be asked to confirm"
                                : undefined
                            }
                            onClick={() => setMarkPaidRow(r)}
                          >
                            Mark paid
                          </button>
                          {r.bookingId ? (
                            <button
                              className="text-sm text-blue-600 hover:underline"
                              onClick={() => navigate(`/bookings/${r.bookingId}?tab=xero`)}
                            >
                              Open
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        )}
        <p className="text-xs text-slate-400">
          From the Xero ledger via the ops sync (≤15 min behind); opening a booking re-reads its invoices live.
          {!canChase &&
            " Client funds compares what this booking's client invoices have collected against what the bill still owes — pay the ready ones."}
          {canChase && " Chases are recorded per invoice — email from here, or log one you made by phone."}
        </p>

        {remindRows && (
          <RemindDialog
            rows={remindRows}
            onClose={() => setRemindRows(null)}
            onSent={() => {
              setRemindRows(null);
              setSelected(new Set());
              load();
            }}
          />
        )}
        {logRow && (
          <LogChaseDialog
            row={logRow}
            onClose={() => setLogRow(null)}
            onDone={() => {
              setLogRow(null);
              load();
            }}
          />
        )}
        {historyRow && <HistoryDialog row={historyRow} onClose={() => setHistoryRow(null)} onChanged={load} />}
        {payRow && (
          <PayViaRevolutDialog invoiceId={payRow.invoiceId} onClose={() => setPayRow(null)} onDone={() => load()} />
        )}
        {markPaidRow && (
          <MarkPaidDialog
            row={markPaidRow}
            onClose={() => setMarkPaidRow(null)}
            onDone={() => {
              setMarkPaidRow(null);
              load();
            }}
          />
        )}
      </div>
    </Content>
  );
}

/* ------------------------------ Chase flows ------------------------------ */

type ReminderPreview = {
  clientEmailsAllowed: boolean;
  invoiceId: string;
  invoiceNumber: string | null;
  brand: string;
  recipient: { email: string; name: string } | null;
  yachtName: string | null;
  amountDue: number;
  currency: string | null;
  dueDate: string | null;
  defaults: { greeting: string; intro: string; body: string; signoff: string };
  subject: string;
  html: string;
};

type ReminderRow = {
  id: string;
  channel: string;
  status: string;
  sentBy: string | null;
  sentAt: string | null;
  recipients: { email: string; name?: string | null }[];
  subject: string | null;
  note: string | null;
  error: string | null;
  removable: boolean;
};

/** Compose + send. One invoice or many: the copy is shared, the amounts and
 *  recipient resolve per invoice server-side (mirrors ops' bulk modal). */
function RemindDialog({ rows, onClose, onSent }: { rows: OpenInvoiceRow[]; onClose: () => void; onSent: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const single = rows.length === 1;
  const [preview, setPreview] = useState<ReminderPreview | null>(null);
  const [subject, setSubject] = useState("");
  const [copy, setCopy] = useState({ greeting: "", intro: "", body: "", signoff: "" });
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missingEmail = rows.filter((r) => !r.clientEmail);

  useEffect(() => {
    // Load the rendered defaults from the first row — the copy applies to all.
    fetch<{ data: ReminderPreview }>(`/api/finance/invoices/${rows[0].invoiceId}/reminder-preview`)
      .then((r) => {
        setPreview(r.data);
        setSubject(r.data.subject);
        setCopy({
          greeting: single ? r.data.defaults.greeting : "Hi there,",
          intro: r.data.defaults.intro,
          body: r.data.defaults.body,
          signoff: r.data.defaults.signoff,
        });
      })
      .catch((e: any) => setError(e?.message ?? "Couldn't build the reminder."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = (toSelf: boolean) => {
    setBusy(true);
    setError(null);
    fetch("/api/finance/reminders/send", "POST", {
      invoiceIds: rows.map((r) => r.invoiceId),
      // Blank greeting on a bulk send keeps each client's own "Hi <name>,".
      editable: { ...copy, greeting: single || copy.greeting.trim() ? copy.greeting : undefined },
      subjectOverride: subject,
      sendToSelf: toSelf,
    })
      .then((r: any) => {
        const d = r?.data ?? {};
        if (toSelf) {
          toast({ title: "Preview sent", description: "Check your inbox — nothing was recorded as a chase." });
          setBusy(false);
          return;
        }
        if (d.redirectedToSender) {
          toast({
            title: `Test send — ${d.sent} delivered to you`,
            description: "No client was emailed and no chase was recorded (this environment blocks client email).",
          });
          onSent();
          return;
        }
        if (d.failed) {
          const first = (d.results ?? []).find((x: any) => !x.ok);
          toast({
            title: `${d.sent} sent, ${d.failed} failed`,
            description: first?.error ?? "Some reminders didn't go out — see the chase history.",
            variant: "destructive",
          });
        } else {
          toast({ title: `Reminder sent to ${d.sent} client${d.sent === 1 ? "" : "s"}`, description: "Recorded against the invoice." });
        }
        onSent();
      })
      .catch((e: any) => {
        setError(e?.message ?? "Send failed.");
        setBusy(false);
      });
  };

  const field = (key: keyof typeof copy, label: string, rows_: number) => (
    <label className="block text-xs font-medium text-slate-500">
      {label}
      <textarea
        value={copy[key]}
        rows={rows_}
        onChange={(e) => setCopy((c) => ({ ...c, [key]: e.target.value }))}
        className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-800 outline-none focus:border-blue-400"
      />
    </label>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{single ? "Send payment reminder" : `Send payment reminder to ${rows.length} clients`}</DialogTitle>
        </DialogHeader>
        {single ? (
          <p className="text-sm text-slate-500">
            {rows[0].contactName ?? "Client"} · {fmtMoney(rows[0].amountDue, rows[0].currency ?? "EUR")} due{" "}
            {fmtDate(rows[0].dueDate)}
            {rows[0].clientEmail ? <> · {rows[0].clientEmail}</> : <span className="text-red-600"> · no email on the booking</span>}
          </p>
        ) : (
          <p className="text-sm text-slate-500">
            Each client gets their own amounts and due date; the wording below is shared.
          </p>
        )}
        {missingEmail.length > 0 && !single && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {missingEmail.length} of {rows.length} have no client email — those will be recorded as failed so you can chase them another way.
          </p>
        )}
        {preview && !preview.clientEmailsAllowed && (
          <p className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
            This environment never emails clients — sending here delivers to you instead, subject-prefixed with the
            address it would have gone to, and records no chase. Use “Log chase” for real chases made elsewhere.
          </p>
        )}
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {!preview ? (
          <p className="text-sm text-slate-400">Building the reminder…</p>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs font-medium text-slate-500">
              Subject
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-800 outline-none focus:border-blue-400"
              />
            </label>
            {field("greeting", single ? "Greeting" : "Greeting (leave blank to use each client's first name)", 1)}
            {field("intro", "Intro", 2)}
            {field("body", "Body", 3)}
            {field("signoff", "Sign-off", 2)}
            <p className="text-xs text-slate-400">
              The outstanding balance, due date and invoice number are added automatically from Xero.
            </p>
            <button className="text-xs text-blue-600 hover:underline" onClick={() => setShowPreview((v) => !v)}>
              {showPreview ? "Hide" : "Show"} rendered email
            </button>
            {showPreview && (
              <iframe
                title="Reminder preview"
                srcDoc={preview.html}
                className="h-80 w-full rounded-lg border"
              />
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" disabled={busy || !preview} onClick={() => send(true)}>
            Send me a preview
          </Button>
          <Button disabled={busy || !preview} onClick={() => send(false)}>
            {busy
              ? "Sending…"
              : preview && !preview.clientEmailsAllowed
                ? "Send (test — to me)"
                : single
                  ? "Send reminder"
                  : `Send ${rows.length} reminders`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Record a chase made outside the app so "last reminder" stays truthful. */
function LogChaseDialog({ row, onClose, onDone }: { row: OpenInvoiceRow; onClose: () => void; onDone: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [sentAt, setSentAt] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const save = () => {
    setBusy(true);
    fetch(`/api/finance/invoices/${row.invoiceId}/reminders/log`, "POST", {
      note: note.trim() || undefined,
      sentAt: new Date(sentAt).toISOString(),
    })
      .then(() => {
        toast({ title: "Chase logged", description: "It now shows as the last reminder on this invoice." });
        onDone();
      })
      .catch((e: any) => {
        toast({ title: "Error", description: e?.message ?? "Couldn't log the chase.", variant: "destructive" });
        setBusy(false);
      });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log a chase</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          {row.contactName ?? "Client"} · {fmtMoney(row.amountDue, row.currency ?? "EUR")} outstanding. Use this when you
          chased by phone, WhatsApp or your own mailbox.
        </p>
        <label className="block text-xs font-medium text-slate-500">
          When
          <input
            type="date"
            value={sentAt}
            onChange={(e) => setSentAt(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-blue-400"
          />
        </label>
        <textarea
          value={note}
          rows={3}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What was said? (optional — e.g. called, paying Friday)"
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
        />
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Log chase"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Every chase on one invoice, newest first. */
function HistoryDialog({ row, onClose, onChanged }: { row: OpenInvoiceRow; onClose: () => void; onChanged: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [history, setHistory] = useState<ReminderRow[] | null>(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    fetch<{ data: ReminderRow[] }>(`/api/finance/invoices/${row.invoiceId}/reminders`)
      .then((r) => setHistory(r.data ?? []))
      .catch(() => setHistory([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.invoiceId]);
  useEffect(load, [load]);

  const remove = (r: ReminderRow) => {
    fetch(`/api/finance/reminders/${r.id}/delete`, "POST")
      .then(() => {
        setDirty(true);
        load();
      })
      .catch((e: any) => toast({ title: "Error", description: e?.message ?? "Couldn't remove it.", variant: "destructive" }));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && (dirty && onChanged(), onClose())}>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Chase history</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          {row.contactName ?? "Client"} · invoice {row.invoiceNumber ?? row.invoiceId.slice(0, 8)}
        </p>
        <div className="space-y-2">
          {history === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-400">No chases recorded.</p>
          ) : (
            history.map((h) => (
              <div key={h.id} className="rounded-lg bg-slate-50 px-3 py-2">
                <p className="flex items-center gap-2 text-sm">
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px] font-medium",
                      h.status === "failed" ? "bg-red-100 text-red-700" : h.channel === "manual" ? "bg-slate-200 text-slate-700" : "bg-green-100 text-green-800",
                    )}
                  >
                    {h.status === "failed"
                      ? "failed"
                      : h.channel === "manual"
                        ? "logged"
                        : h.channel === "platform"
                          ? "auto instalment"
                          : "emailed"}
                  </span>
                  <span className="text-slate-700">{fmtDate(h.sentAt)}</span>
                  <span className="text-xs text-slate-400">{fmtRelative(h.sentAt)}</span>
                </p>
                {h.recipients.length > 0 && (
                  <p className="mt-0.5 text-xs text-slate-500">to {h.recipients.map((x) => x.email).join(", ")}</p>
                )}
                {h.subject && <p className="mt-0.5 text-xs text-slate-500">{h.subject}</p>}
                {h.note && <p className="mt-0.5 text-sm text-slate-700">{h.note}</p>}
                {h.error && <p className="mt-0.5 text-xs text-red-600">{h.error}</p>}
                <p className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                  {h.sentBy ?? (h.channel === "platform" ? "sent by the booking's payment schedule" : "—")}
                  {h.removable && (
                    <button className="hover:text-red-600 hover:underline" onClick={() => remove(h)}>
                      remove
                    </button>
                  )}
                </p>
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => (dirty && onChanged(), onClose())}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Settled documents (ops "Sent payments"): what has already gone out. */
function SettledTable({ rows, who, onOpen }: { rows: SettledRow[] | null; who: string; onOpen: (bookingId: string) => void }) {
  return (
    <div className="overflow-x-auto rounded-xl border bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">{who}</th>
            <th className="px-3 py-2">Deal / yacht</th>
            <th className="px-3 py-2">Brand</th>
            <th className="px-3 py-2">Invoice</th>
            <th className="px-3 py-2">Dated</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows === null ? (
            <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Nothing settled yet.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.invoiceId} className="border-t">
                <td className="px-3 py-2 font-medium">{r.contactName ?? "—"}</td>
                <td className="max-w-[260px] truncate px-3 py-2 text-slate-600">
                  {[r.dealName, r.yachtName].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-3 py-2 capitalize text-slate-500">{r.brand ?? "—"}</td>
                <td className="px-3 py-2 text-slate-500">{r.invoiceNumber ?? "—"}</td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">{fmtDate(r.invoiceDate)}</td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">{fmtMoney(r.amountTotal, r.currency ?? "EUR")}</td>
                <td className="px-3 py-2">
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                    {r.status ?? "PAID"}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {r.bookingId && (
                    <button className="text-sm text-blue-600 hover:underline" onClick={() => onOpen(r.bookingId!)}>
                      Open
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

type MarkPaidOptions = {
  invoiceId: string;
  invoiceNumber: string | null;
  invoiceType: string | null;
  contactName: string | null;
  brand: string;
  currency: string | null;
  amountDue: number;
  amountTotal: number;
  bankAccounts: {
    default: { accountId: string; name: string; currency: string | null } | null;
    candidates: { accountId: string; name: string; currency: string | null }[];
  };
};

/** Record a payment against the invoice in Xero (ops Mark-paid parity). Use
 *  when money arrived outside the matched-payments flow. */
export function MarkPaidDialog({ row, onClose, onDone }: { row: OpenInvoiceRow; onClose: () => void; onDone: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [options, setOptions] = useState<MarkPaidOptions | null>(null);
  const [accountId, setAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch<{ data: MarkPaidOptions }>(`/api/finance/invoices/${row.invoiceId}/mark-paid-options`)
      .then((r) => {
        setOptions(r.data);
        setAccountId(r.data.bankAccounts.default?.accountId ?? "");
        setAmount(String(r.data.amountDue));
      })
      .catch((e: any) => setError(e?.message ?? "Couldn't load the bank accounts — is Xero connected for this brand?"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.invoiceId]);

  const save = () => {
    setBusy(true);
    setError(null);
    fetch(`/api/finance/invoices/${row.invoiceId}/mark-paid`, "POST", {
      accountId,
      amount: Number(amount) || undefined,
      date: new Date(date).toISOString(),
      reference: reference.trim() || undefined,
    })
      .then((r: any) => {
        const left = r?.data?.amountDue ?? 0;
        toast({
          title: "Payment recorded in Xero",
          description: left > 0 ? `${fmtMoney(left, row.currency ?? "EUR")} still outstanding.` : "Invoice is now fully paid.",
        });
        onDone();
      })
      .catch((e: any) => {
        setError(e?.message ?? "Xero rejected the payment.");
        setBusy(false);
      });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark paid</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          {row.contactName ?? "Client"} · invoice {row.invoiceNumber ?? row.invoiceId.slice(0, 8)} ·{" "}
          {fmtMoney(row.amountDue, row.currency ?? "EUR")} outstanding
        </p>
        {row.supplierBankOnFile === false && (
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            No bank details on file for this supplier — add them on the booking page before paying.
          </p>
        )}
        {(row.fundingState === "waiting" || row.fundingState === "client_overdue") && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            The client still owes {fmtMoney(row.shortfall ?? 0, row.currency ?? "EUR")} against this bill
            {row.fundingState === "client_overdue" && " and their invoice is overdue"} — paying now spends HELM's own
            money. Continue only if that's intended.
          </p>
        )}
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        {!options ? (
          <p className="text-sm text-slate-400">Loading bank accounts…</p>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs font-medium text-slate-500">
              Paid into
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-blue-400"
              >
                <option value="">Choose bank account…</option>
                {options.bankAccounts.candidates.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.name}{a.currency ? ` (${a.currency})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs font-medium text-slate-500">
                Amount
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal tabular-nums outline-none focus:border-blue-400"
                />
              </label>
              <label className="block text-xs font-medium text-slate-500">
                Date received
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-blue-400"
                />
              </label>
            </div>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Reference (optional)"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
            />
            <p className="text-xs text-slate-400">
              This writes a payment straight into {options.brand.toUpperCase()} Xero. For money that arrived on a card or
              bank feed, match it in the payments inbox instead so the ledger row is linked too.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy || !options || !accountId || !(Number(amount) > 0)} onClick={save}>
            {busy ? "Recording…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const Receivables = () => <OpenInvoicesScreen kind="receivables" />;
